/**
 * Wayfinding signs that can be placed on a floor plan, plus the other small
 * vocabularies (table features, occasions, booking statuses) that the server,
 * the web app and the AI agent API all need to agree on.
 */

export type SignCategory = 'safety' | 'accessible' | 'facility' | 'service' | 'leisure' | 'outdoor';

export interface AmenityKind {
  kind: string;
  /** Default text on the sign. Venues can override it per sign ("Ladies"). */
  label: string;
  category: SignCategory;
  /** Pictogram key, resolved by the web app's sign renderer. */
  icon: string;
  /** How the AI agent API describes it: "the toilets", "the bar". */
  spoken: string;
}

export const AMENITY_KINDS: readonly AmenityKind[] = [
  { kind: 'toilets', label: 'Toilets', category: 'facility', icon: 'wc', spoken: 'the toilets' },
  { kind: 'toilets_ladies', label: 'Ladies', category: 'facility', icon: 'woman', spoken: 'the ladies' },
  { kind: 'toilets_gents', label: 'Gents', category: 'facility', icon: 'man', spoken: 'the gents' },
  { kind: 'toilets_accessible', label: 'Accessible toilet', category: 'accessible', icon: 'wheelchair', spoken: 'the accessible toilet' },
  { kind: 'baby_change', label: 'Baby changing', category: 'facility', icon: 'baby', spoken: 'the baby changing room' },
  { kind: 'bar', label: 'Bar', category: 'service', icon: 'beer', spoken: 'the bar' },
  { kind: 'cocktail_bar', label: 'Cocktail bar', category: 'service', icon: 'martini', spoken: 'the cocktail bar' },
  { kind: 'order_point', label: 'Order here', category: 'service', icon: 'utensils', spoken: 'the food ordering point' },
  { kind: 'reception', label: 'Host stand', category: 'service', icon: 'bell', spoken: 'the host stand' },
  { kind: 'kitchen', label: 'Kitchen', category: 'service', icon: 'chef', spoken: 'the kitchen' },
  { kind: 'entrance', label: 'Entrance', category: 'service', icon: 'entrance', spoken: 'the entrance' },
  { kind: 'exit', label: 'Exit', category: 'safety', icon: 'exit', spoken: 'the exit' },
  { kind: 'fire_exit', label: 'Fire exit', category: 'safety', icon: 'fire-exit', spoken: 'the fire exit' },
  { kind: 'first_aid', label: 'First aid', category: 'safety', icon: 'first-aid', spoken: 'the first aid point' },
  { kind: 'stairs', label: 'Stairs', category: 'facility', icon: 'stairs', spoken: 'the stairs' },
  { kind: 'lift', label: 'Lift', category: 'accessible', icon: 'lift', spoken: 'the lift' },
  { kind: 'step_free', label: 'Step-free access', category: 'accessible', icon: 'wheelchair', spoken: 'the step-free entrance' },
  { kind: 'beer_garden', label: 'Beer garden', category: 'outdoor', icon: 'garden', spoken: 'the beer garden' },
  { kind: 'smoking_area', label: 'Smoking area', category: 'outdoor', icon: 'smoking', spoken: 'the smoking area' },
  { kind: 'parking', label: 'Car park', category: 'outdoor', icon: 'parking', spoken: 'the car park' },
  { kind: 'bike_rack', label: 'Bike rack', category: 'outdoor', icon: 'bike', spoken: 'the bike rack' },
  { kind: 'cloakroom', label: 'Cloakroom', category: 'facility', icon: 'hanger', spoken: 'the cloakroom' },
  { kind: 'cash_machine', label: 'Cash machine', category: 'facility', icon: 'cash', spoken: 'the cash machine' },
  { kind: 'water', label: 'Water station', category: 'facility', icon: 'water', spoken: 'the water station' },
  { kind: 'stage', label: 'Live music', category: 'leisure', icon: 'music', spoken: 'the stage' },
  { kind: 'sports_tv', label: 'Sports TV', category: 'leisure', icon: 'tv', spoken: 'the big screen' },
  { kind: 'fireplace', label: 'Fireplace', category: 'leisure', icon: 'fire', spoken: 'the fireplace' },
  { kind: 'pool_table', label: 'Pool table', category: 'leisure', icon: 'pool', spoken: 'the pool table' },
  { kind: 'darts', label: 'Darts', category: 'leisure', icon: 'darts', spoken: 'the dartboard' },
  { kind: 'games', label: 'Games', category: 'leisure', icon: 'dice', spoken: 'the games corner' },
  { kind: 'play_area', label: 'Play area', category: 'leisure', icon: 'play', spoken: "the children's play area" },
  { kind: 'dog_area', label: 'Dogs welcome', category: 'leisure', icon: 'dog', spoken: 'the dog-friendly area' },
  { kind: 'info', label: 'Information', category: 'service', icon: 'info', spoken: 'the information point' },
];

const AMENITY_BY_KIND = new Map(AMENITY_KINDS.map((a) => [a.kind, a]));

export function amenityKind(kind: string): AmenityKind | undefined {
  return AMENITY_BY_KIND.get(kind);
}

export function isAmenityKind(kind: string): boolean {
  return AMENITY_BY_KIND.has(kind);
}

export const SIGN_CATEGORY_LABELS: Record<SignCategory, string> = {
  safety: 'Safety',
  accessible: 'Accessibility',
  facility: 'Facilities',
  service: 'Bar & service',
  leisure: 'Things to do',
  outdoor: 'Outside',
};

export interface TableFeature {
  key: string;
  label: string;
}

export const TABLE_FEATURES: readonly TableFeature[] = [
  { key: 'window', label: 'By the window' },
  { key: 'booth', label: 'Booth' },
  { key: 'sofa', label: 'Sofa seating' },
  { key: 'high_top', label: 'High table' },
  { key: 'fireplace', label: 'By the fire' },
  { key: 'quiet', label: 'Quiet corner' },
  { key: 'garden', label: 'Outdoors' },
  { key: 'covered', label: 'Covered & heated' },
  { key: 'accessible', label: 'Wheelchair accessible' },
  { key: 'dog_friendly', label: 'Dogs welcome' },
  { key: 'tv_view', label: 'View of the screens' },
  { key: 'family', label: 'Family friendly' },
  { key: 'private', label: 'Semi-private' },
];

const FEATURE_BY_KEY = new Map(TABLE_FEATURES.map((f) => [f.key, f]));

export function tableFeatureLabel(key: string): string {
  return FEATURE_BY_KEY.get(key)?.label ?? key.replace(/_/g, ' ');
}

export function isTableFeature(key: string): boolean {
  return FEATURE_BY_KEY.has(key);
}

export const TABLE_SHAPES = ['round', 'square', 'rect', 'oval', 'booth', 'high'] as const;
export type TableShape = (typeof TABLE_SHAPES)[number];

export const OCCASIONS = [
  { key: 'birthday', label: 'Birthday' },
  { key: 'anniversary', label: 'Anniversary' },
  { key: 'date', label: 'Date night' },
  { key: 'celebration', label: 'Celebration' },
  { key: 'business', label: 'Business' },
  { key: 'family', label: 'Family get-together' },
  { key: 'sports', label: 'Watching the match' },
] as const;

export const BOOKING_STATUSES = ['pending', 'confirmed', 'seated', 'completed', 'cancelled', 'no_show'] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  seated: 'Seated',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No-show',
};

export const BOOKING_SOURCES = ['online', 'staff', 'phone', 'walk_in', 'api', 'ai_agent'] as const;
export type BookingSource = (typeof BOOKING_SOURCES)[number];

export const BOOKING_SOURCE_LABELS: Record<BookingSource, string> = {
  online: 'Online',
  staff: 'Staff',
  phone: 'Phone',
  walk_in: 'Walk-in',
  api: 'API',
  ai_agent: 'AI phone agent',
};

export const ROLES = ['owner', 'admin', 'manager', 'staff'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  staff: 'Staff',
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner: 'Full control of the company, including other owners.',
  admin: 'Venues, team, API keys and all settings.',
  manager: 'Bookings, guests, floor plans and venue settings for their venues.',
  staff: 'Takes and manages bookings and looks up guests at their venues.',
};

export const API_SCOPES = [
  'availability:read',
  'bookings:read',
  'bookings:write',
  'customers:read',
  'customers:write',
] as const;
export type ApiScope = (typeof API_SCOPES)[number];
